import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';

//- material
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import CheckIcon from '@material-ui/icons/Check';

import { valueToDisplay } from '../../libs/regions';

//- local
import TerminalsContainer from '../../containers/logistics/Terminals';
import EnhancedTableHead from '../../components/EnhancedTableHead';
import { isFiltered, sortTable, formatPrimaryContact } from '../../libs/utility';

import { getSharedStyles } from '../../themes';
import AuthUserContainer from '../../containers/AuthUser';
import FMPHeadline from '../../components/ui/FMPHeadline';
const styles = (theme) => ({
	...getSharedStyles(theme),
	button: {
		verticalAlign: 'middle',
		padding: '8px 10px',
	},
	addButton: {
		verticalAlign: 'middle',
		textDecoration: 'none',
		marginBottom: 10,
		borderRadius: 100,
	},
	formControl: {
		fontSize: 14,
		marginLeft: 20,
		flex: 1,
	},
	title: {
		display: 'flex',
		alignItems: 'flex-end',
	},
	dataLabel: {
		textTransform: 'uppercase',
		fontSize: 12,
		paddingRight: 4,
		color: '#666',
	},
});

class Terminals extends Component {
	constructor(props) {
		super(props);

		this.state = {
			isViewDialogOpen: false,
			viewStation: {},
			isDcDialogOpen: false,
			dcStation: {},
			order: 'asc',
			orderBy: 'name',
			search: '',
		};
	}

	componentDidMount() {
		if (!this.props.terminals.length) {
			this.props.searchTerminals();
		}
	}

	handleSearch = (event) => {
		this.setState({ search: event.target.value });
	};

	handleEdit = (terminalId) => {
		this.props.history.push(`/logistics/terminal/update/${terminalId}`);
	};

	handleRequestSort = (property) => {
		const orderBy = property;
		let order = 'desc';

		if (this.state.orderBy === property && this.state.order === /** @type {string} */ 'desc') {
			order = 'asc';
		}
		this.setState({ order, orderBy });
	};

	render() {
		const { classes, terminals, supplierMap, authUser, isExternalLogistics } = this.props;
		const { order, orderBy, search } = this.state;
		if (!terminals || !supplierMap || !authUser) {
			return null;
		}
		const canMakeUpdates = _.get(authUser, ['permissions', 'canMakeUpdates']);

		let data = terminals.reduce((result, terminal) => {
			const {
				supplierProducts,
				bulkSuppliers,
				contacts,
				terminalId,
				name,
				region,
				deliveryEmails,
				scheduleEmails,
				quickbooksClassId,
				autoInventoryRollover,
			} = terminal;

			const supplierList = [];
			supplierProducts.forEach((sp) => {
				const supplier = supplierMap[sp.supplierId];
				const supplierString = supplier ? `${supplier.name} (${sp.product})` : 'Missing Supplier';
				supplierList.push({
					type: 'S:',
					value: supplierString,
				});
			});

			bulkSuppliers.forEach((bs) => {
				const supplier = supplierMap[bs.supplierId];
				const supplierString = supplier ? `${supplier.name} (${bs.product})` : 'Missing Supplier';
				supplierList.push({
					type: 'B:',
					value: supplierString,
				});
			});

			let supplierStrings = supplierList.map((s, i) => {
				return (
					<div key={i}>
						<span className={classes.dataLabel}>{s.type}</span>
						<span>{s.value}</span>
					</div>
				);
			});

			const primaryContact = formatPrimaryContact(_.first(contacts));
			const hasDeliveryEmails = _.isEmpty(deliveryEmails) ? '-' : <CheckIcon />;
			const hasScheduleEmails = _.isEmpty(scheduleEmails) ? '-' : <CheckIcon />;
			const hasClassId = _.isEmpty(quickbooksClassId) ? '-' : <CheckIcon />;
			const hasAutoRollover = autoInventoryRollover ? <CheckIcon /> : '-';

			const data = {
				terminalId,
				name,
				region,
				hasClassId,
				hasDeliveryEmails,
				hasScheduleEmails,
				hasAutoRollover,
				regionDisplay: valueToDisplay(region),
				primaryContact,
				supplierStrings,
			};

			if (isFiltered({ search, data, fields: ['name', 'regionDisplay'] })) {
				return result;
			}

			result.push(data);
			return result;
		}, []);
		data = sortTable({ data, order, orderBy });

		const columnData = [
			{ id: 'name', label: 'Name' },
			{ id: 'region', label: 'Region' },
			{ id: 'primaryContact', label: 'Primary Contact' },
			{ id: 'supplierString', label: 'Suppliers & Products' },
			{ id: 'hasClassId', labels: ['QB Class', 'Linked'] },
			{ id: 'hasDeliveryEmails', labels: ['Delivery', 'Emails'] },
			{ id: 'hasScheduleEmails', labels: ['Schedule', 'Emails'] },
			{ id: 'hasAutoRollover', labels: ['Auto', 'Rollover'] },
			{ id: 'edit', align: 'center', label: isExternalLogistics ? 'View' : 'Edit' },
		];

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<FMPHeadline
						label="Terminals"
						value={this.state.search}
						onChange={this.handleSearch}
						buttonText="Add Terminal"
						url="/logistics/terminal/add"
						canMakeUpdates={isExternalLogistics ? false : canMakeUpdates}
					/>

					<Paper className={classes.paper}>
						<Table stickyHeader size="small" className={classes.table}>
							<EnhancedTableHead
								columnData={columnData}
								order={order}
								orderBy={orderBy}
								onRequestSort={this.handleRequestSort}
							/>
							<TableBody>
								{data.map((row) => {
									return (
										<TableRow hover className={classes.row} key={row.terminalId}>
											<TableCell>{row.name}</TableCell>
											<TableCell>{row.regionDisplay}</TableCell>
											<TableCell>{row.primaryContact}</TableCell>
											<TableCell>{row.supplierStrings}</TableCell>
											<TableCell>{row.hasClassId}</TableCell>
											<TableCell>{row.hasDeliveryEmails}</TableCell>
											<TableCell>{row.hasScheduleEmails}</TableCell>
											<TableCell>{row.hasAutoRollover}</TableCell>
											{canMakeUpdates && (
												<TableCell style={{ whiteSpace: 'nowrap' }}>
													<Button
														className={classes.button}
														onClick={() => this.handleEdit(row.terminalId)}
													>
														{isExternalLogistics && 'View'}
														{!isExternalLogistics && 'Edit'}
													</Button>
												</TableCell>
											)}
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</Paper>
				</Grid>
			</Grid>
		);
	}
}

Terminals.propTypes = {
	classes: PropTypes.object.isRequired,
	searchTerminals: PropTypes.func.isRequired,
	terminals: PropTypes.array,
	supplierMap: PropTypes.object,
	order: PropTypes.string,
	orderBy: PropTypes.string,
	history: PropTypes.object,
	authUser: PropTypes.object,
	isExternalLogistics: PropTypes.bool.isRequired,
};

export default _.flow([withStyles(styles), TerminalsContainer, AuthUserContainer])(Terminals);
